/**
 * PURPOSE:
 * Hook Socket.IO para visualizar el estado vivo de vóley en HAYLLIS.
 *
 * ROUTE:
 * src/pages/VolleyballViewer/hooks/useVolleyballViewerSocket.js
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { getViewerPublicSnapshot, VOLLEYBALL_VIEWER_SOCKET_URL } from "../services/volleyballViewerPublicService.js";

export function useVolleyballViewerSocket({ matchId, session, enabled = true, onKicked, onDisabled }) {
  const [connectionState, setConnectionState] = useState("idle");
  const [payload, setPayload] = useState(null);
  const [timeoutOverlay, setTimeoutOverlay] = useState(null);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);

  const applyPayload = useCallback((next) => {
    if (!next) return;
    setPayload((prev) => {
      if (!prev) return next;
      if (Number(next.seq || 0) >= Number(prev.seq || 0)) return next;
      return prev;
    });
  }, []);

  const resync = useCallback(async () => {
    if (!matchId) return;
    setConnectionState((prev) => prev === "connected" ? "syncing" : prev);
    try {
      const snapshot = await getViewerPublicSnapshot(matchId, session);
      applyPayload(snapshot);
      setError(null);
      setConnectionState("connected");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "No se pudo resincronizar.");
      setConnectionState("stale");
    }
  }, [applyPayload, matchId, session]);

  useEffect(() => {
    if (!enabled || !matchId || !session?.sessionToken) return undefined;
    const socket = io(VOLLEYBALL_VIEWER_SOCKET_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnectionState("connecting");
      socket.emit("viewer:join", {
        matchId: Number(matchId),
        sessionId: session.sessionId,
        sessionToken: session.sessionToken,
        deviceId: session.deviceId,
      });
    });

    socket.on("viewer:joined", (data) => {
      setConnectionState("connected");
      setError(null);
      setTimeoutOverlay(null);
      applyPayload(data?.payload);
    });

    socket.on("viewer:state_updated", (nextPayload) => {
      setConnectionState("connected");
      setError(null);
      applyPayload(nextPayload);
    });

    socket.on("viewer:timeout_overlay_show", (data) => {
      const seconds = Number(data?.seconds || 30);
      const safeSeconds = Number.isFinite(seconds) ? Math.max(1, seconds) : 30;
      const team = String(data?.team || "").trim().toUpperCase();
      if (!["A", "B"].includes(team)) return;

      setTimeoutOverlay({
        team,
        seconds: safeSeconds,
        startedAt: Date.now(),
        nonce: data?.nonce || `${Date.now()}`,
      });
    });

    socket.on("viewer:timeout_overlay_hide", () => {
      setTimeoutOverlay(null);
    });

    socket.on("viewer:error", (data) => {
      setError(data?.message || "Error de conexión.");
      if (data?.code === "ROOM_DISABLED") onDisabled?.();
    });

    socket.on("viewer:kicked", (data) => {
      setConnectionState("kicked");
      setTimeoutOverlay(null);
      setError(data?.reason === "REPLACED_BY_SAME_DEVICE" ? "Se abrió otra pestaña de este dispositivo." : "La mesa desconectó este dispositivo.");
      onKicked?.(data);
    });

    socket.on("viewer:room_disabled", (data) => {
      setConnectionState(data?.reason === "OFFICIALIZED" ? "officialized" : "disabled");
      setTimeoutOverlay(null);
      setError(data?.reason === "OFFICIALIZED" ? "Partido oficializado. Visualización cerrada." : "La mesa desactivó el módulo.");
      onDisabled?.(data);
    });

    socket.on("disconnect", () => {
      setConnectionState("reconnecting");
      setTimeoutOverlay(null);
    });

    socket.on("connect_error", () => {
      setConnectionState("reconnecting");
      setTimeoutOverlay(null);
    });

    const pingId = window.setInterval(() => {
      if (socket.connected) socket.emit("viewer:ping");
    }, 20000);

    return () => {
      window.clearInterval(pingId);
      socket.disconnect();
      socketRef.current = null;
      setTimeoutOverlay(null);
    };
  }, [applyPayload, enabled, matchId, onDisabled, onKicked, session]);

  const requestSocketResync = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("viewer:resync_request");
    } else {
      resync();
    }
  }, [resync]);

  return {
    connectionState,
    payload,
    timeoutOverlay,
    error,
    resync,
    requestSocketResync,
    setPayload,
    setTimeoutOverlay,
  };
}
